import { observer } from "mobx-react-lite";
import * as React from "react";
import { CellInternalBox } from "src/components/Sheet2/components/CellInternalBox";
import { KeyDef } from "src/components/Sheet2/isSymbolKey";
import { PropsCell } from "src/components/Sheet2/PropsCell2";

type PropsCell2WithModal = {
  cell: PropsCell;
  btnRef: React.RefObject<HTMLElement>;
  children?: React.ReactNode | React.ReactNode[]; // Включает компоненты кнопки и модалки
  isKeySpecial?(e: KeyDef): boolean;
  onFocus?(e: FocusEvent): void;
};

/**
 * Имеется в виду не конкретно компонент Modal, а любое действие, где редактирование значения происходит в отдельном окошке.
 * Обеспечивает обработку фокуса и навигации клавишами.
 * ref = React.useRef<HTMLElement>(null);  ...
 * <Cell2WithModal btnRef={ref} ...> ...
 *     <Button ref={ref} /> ...
 * </Cell2WithModal>
 */
export const Cell2WithModal: React.FC<PropsCell2WithModal> = observer(
  (props) => {
    const { cell, btnRef, children, isKeySpecial, onFocus } = props;
    const { cellKey, pos, store, value } = cell;

    // Используется функция а не константа, т.к. значение current меняется где-то снаружи. И компонент при этом не перерисовывается.
    const button = () => btnRef.current;

    const onKey = (e: KeyDef) => {
      e.stopPropagation();
      if (isKeySpecial?.(e)) return;
      store.onKeyDown(cellKey, e);
    };
    const handleFocus = (e: FocusEvent) => {
      store.activate(cellKey, false);
      onFocus?.(e);
    };

    React.useEffect(() => {
      store.updateCell({
        ...cell,
        focus: () => {
          button()?.focus?.();
        },
        blur: () => {
          button()?.blur?.();
        },
      });
      button()?.addEventListener("focus", handleFocus);
      button()?.addEventListener("keydown", onKey);
      return () => {
        button()?.removeEventListener("keydown", onKey);
        button()?.removeEventListener("focus", handleFocus);
      };
    });

    React.useEffect(() => {
      store.setSrcCellValue(cellKey, value);
    }, [value]);

    return (
      <CellInternalBox store={store} cellKey={cellKey} pos={pos}>
        {children}
      </CellInternalBox>
    );
  },
);
